import { NavLink, useLocation } from "react-router-dom";
import {
  HomeIcon,
  UsersIcon,
  CalendarIcon,
  FileTextIcon,
  SettingsIcon,
  CreditCardIcon,
  ClipboardListIcon,
  BedDouble,
  Calculator,
  BarChart3,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useSidebar } from "@/context/SidebarContext";
import { useAuth } from "@/context/AuthContext";
import { usePermission } from "@/hooks/usePermission";
import { cn } from "@/lib/utils";
import { useState, useCallback, useRef } from "react";
import React from "react";

export default function Sidebar() {
  const location = useLocation();
  const { currentUser } = useAuth();
  const { hasFeature, can } = usePermission();
  const { collapsed, toggle, isInitialized } = useSidebar();
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const userRole = currentUser?.role || "doctor";
  const hasClinicalProfile = !!currentUser?.doctor_id;
  // Route based on clinical profile (doctor_id), not just role
  // Staff and admins without doctor_id → front-desk routes
  // Anyone with doctor_id (doctor or doctor-admin) → clinical routes
  const dashboardRoute = userRole === "staff" ? "/frontdesk" : "/";
  const appointmentsRoute = hasClinicalProfile ? "/appointments" : "/frontdesk/appointments";
  const patientsRoute = hasClinicalProfile ? "/patients" : "/frontdesk/patients";

  const navItems = [
    { label: "Dashboard",    icon: HomeIcon,          to: dashboardRoute, roles: ["doctor", "staff", "admin"], exactMatch: true },
    { label: "Patients",     icon: UsersIcon,         to: patientsRoute,  roles: ["doctor", "staff", "admin"], permission: "patients:read", requireClinical: true },
    { label: "Appointments", icon: CalendarIcon,      to: appointmentsRoute, roles: ["doctor", "staff", "admin"], permission: "appointments:read" },
    { label: "Prescriptions",icon: FileTextIcon,      to: "/prescriptions",roles: ["doctor", "admin"], permission: "prescriptions:read", requireClinical: true },
    ...(hasFeature("peds_calculator")
      ? [{ label: "Peds Calculator", icon: Calculator, to: "/peds-calculator", roles: ["doctor", "admin"], requireClinical: true }]
      : []),
    { label: "Billing",      icon: CreditCardIcon,    to: "/billing",      roles: ["doctor", "admin", "staff"], permission: "billing:read" },
    { label: "IPD",          icon: BedDouble,         to: "/ipd",          roles: ["doctor", "admin", "staff"], permission: "ipd:read" },
    ...(hasFeature("reports")
      ? [{ label: "Reports", icon: BarChart3, to: "/reports", roles: ["doctor", "admin", "staff"], permission: "reports:read" }]
      : []),
    { label: "Templates",    icon: ClipboardListIcon, to: "/templates",    roles: ["doctor", "admin"], permission: "prescriptions:read", requireClinical: true },
    { label: "Settings",     icon: SettingsIcon,      to: "/settings",     roles: ["admin", "doctor"], permission: "settings:read" },
  ];

  const isActiveRoute = (to: string, exactMatch?: boolean) => {
    if (exactMatch) return location.pathname === to;
    return location.pathname === to || location.pathname.startsWith(to + "/");
  };

  const visibleNavItems = navItems.filter((item) => {
    // Check role
    if (!item.roles.includes(userRole)) return false;
    // Check permission
    if (item.permission && !can(item.permission)) return false;
    // Check clinical profile requirement (for clinical routes)
    if ((item as any).requireClinical && !hasClinicalProfile) return false;
    return true;
  });

  if (currentUser?.is_platform_admin) {
    visibleNavItems.push({ label: "Platform Admin", icon: ShieldCheck, to: "/platform-admin", roles: [], requireClinical: false });
  }

  // Per-icon label tooltip (collapsed state only) — a small floating hint, not a
  // layout-affecting overlay, so it carries none of the content-clipping risk that
  // expanding the whole sidebar on hover did.
  const handleItemMouseEnter = useCallback((label: string) => {
    if (collapsed) {
      if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
      setActiveTooltip(label);
    }
  }, [collapsed]);

  const handleItemMouseLeave = useCallback(() => {
    if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
    setActiveTooltip(null);
  }, []);

  const handleToggleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveTooltip(null);
    toggle();
  }, [toggle]);

  return (
      <aside
          aria-label="Sidebar navigation"
          className={cn(
              // Use CSS variable for top — set by MainLayout's ResizeObserver
              // Falls back to 60px if variable not yet set
              "fixed left-0 z-40 border-r border-border overflow-y-auto overflow-x-hidden",
              "sidebar-ideal sidebar-no-shift bg-[hsl(var(--sidebar-bg))] text-[hsl(var(--sidebar-foreground))] shadow-sm",
              !isInitialized && "sidebar-loading",
              collapsed ? "w-12" : "w-44 lg:w-[180px] xl:w-[200px] 2xl:w-[210px]"
          )}
          style={{
            top:    "var(--topbar-height, 60px)",
            height: "calc(100vh - var(--topbar-height, 60px))",
          }}
      >
        <nav className="py-4" role="navigation" aria-label="Main navigation">
          <button
              type="button"
              onClick={handleToggleClick}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-expanded={!collapsed}
              className={cn(
                  "flex items-center w-full h-10 mb-2 pb-2 border-b border-border/60 text-[hsl(var(--sidebar-foreground)/0.65)]",
                  "hover:bg-[hsl(var(--sidebar-accent))] hover:text-[hsl(var(--sidebar-foreground))] transition-colors",
                  collapsed ? "justify-center px-2" : "justify-end px-4"
              )}
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
          <ul className="space-y-1">
            {visibleNavItems.map(({ label, icon: Icon, to, exactMatch }) => {
              const isActive    = isActiveRoute(to, exactMatch);
              const showTooltip = collapsed && activeTooltip === label;
              const isDivider   = label === "Settings" || label === "Debug Logs";

              return (
                  <React.Fragment key={to}>
                    {isDivider && (
                        <li><div className="my-2 border-t border-border mx-4" /></li>
                    )}
                    <li key={label}>
                      <NavLink
                          to={to}
                          className={cn(
                              "flex items-center text-sm font-medium group relative sidebar-focus sidebar-item-hover overflow-hidden",
                              "transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-inset",
                              collapsed ? "px-2 py-3 justify-center text-xs" : "px-6 py-3 text-xs",
                              isActive
                                  ? "bg-[hsl(var(--sidebar-accent))] text-[hsl(var(--sidebar-active))] font-semibold rounded-lg sidebar-item-active"
                                  : "text-[hsl(var(--sidebar-foreground)/0.65)] hover:bg-[hsl(var(--sidebar-accent))] hover:text-[hsl(var(--sidebar-foreground))]"
                          )}
                          aria-current={isActive ? "page" : undefined}
                          onMouseEnter={() => handleItemMouseEnter(label)}
                          onMouseLeave={handleItemMouseLeave}
                          style={isActive ? { boxShadow: "0 2px 8px 0 rgba(0,0,0,0.15)" } : {}}
                      >
                        <Icon className={cn(
                            "flex-shrink-0 transition-all duration-150 sidebar-icon",
                            collapsed ? "mx-auto w-4 h-4" : "mr-3 w-4 h-4",
                            isActive ? "text-[hsl(var(--sidebar-active))]" : ""
                        )} />
                        <span className={cn(
                            "transition-all duration-200 whitespace-nowrap sidebar-text-smooth",
                            collapsed ? "sidebar-text-hidden" : "sidebar-text-visible"
                        )}>
                      {label}
                    </span>
                        {showTooltip && (
                            <div className="sidebar-tooltip fixed left-20 px-3 py-2 bg-popover text-popover-foreground text-xs rounded-lg shadow-lg border border-border whitespace-nowrap z-[100] pointer-events-none">
                              {label}
                              <div className="absolute top-1/2 left-0 transform -translate-y-1/2 -translate-x-2 w-0 h-0 border-t-4 border-b-4 border-r-4 border-transparent border-r-popover" />
                            </div>
                        )}
                      </NavLink>
                    </li>
                  </React.Fragment>
              );
            })}
          </ul>
        </nav>
      </aside>
  );
}
